/**
 * Helper functions for formatting agent SSE events into human-readable
 * summaries displayed in the chat UI.
 */

export function truncateUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.length > 50 ? '…' + u.pathname.slice(-40) : u.pathname;
    return u.hostname + path;
  } catch {
    return url.length > 60 ? url.slice(0, 57) + '…' : url;
  }
}

export function buildToolCallSummary(rest) {
  const parts = [];
  if (rest.search_text) parts.push(`search: "${rest.search_text}"`);
  if (rest.css_selector) parts.push(`selector: ${rest.css_selector}`);
  if (rest.sample_index != null) parts.push(`sample #${rest.sample_index}`);
  if (rest.failing_count != null) parts.push(`${rest.failing_count} failing, ${rest.passing_count ?? 0} passing`);
  if (rest.skipped_false_positives) parts.push(`${rest.skipped_false_positives} skipped (false positive)`);
  if (rest.reason && rest.tool === 'mark_false_positive') parts.push(rest.reason);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function buildToolResultSummary(rest) {
  const parts = [];
  if (rest.marked != null) parts.push(rest.marked ? 'marked as false positive' : 'not marked');
  if (rest.match_count != null) parts.push(`${rest.match_count} match(es)`);
  if (rest.selector_found != null) parts.push(`selector found: ${rest.selector_found}`);
  if (rest.value != null) parts.push(`value: "${String(rest.value).slice(0, 80)}"`);
  if (rest.expected_value != null) parts.push(`expected: "${String(rest.expected_value).slice(0, 80)}"`);
  if (rest.all_pass != null) parts.push(rest.all_pass ? 'all pass' : `${rest.failing_count ?? '?'} failing`);
  if (rest.success != null) parts.push(rest.success ? 'submitted' : `${rest.failure_count ?? '?'} failures`);
  if (rest.skipped_false_positives) parts.push(`${rest.skipped_false_positives} skipped (false positive)`);
  if (rest.error) parts.push(`error: ${rest.error}`);
  if (rest.verdict) parts.push(rest.verdict === 'VERIFIED' ? 'verified' : 'not verified');
  if (rest.reason) parts.push(rest.reason);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Convert a raw SSE event object into { title, details } for display.
 */
export function formatEventDetails(event) {
  const { type, ...rest } = event;

  switch (type) {
    case 'tool_call':
      return {
        title: rest.tool || 'unknown',
        details: buildToolCallSummary(rest),
      };
    case 'tool_result':
      return {
        title: `${rest.tool || 'unknown'} returned`,
        details: buildToolResultSummary(rest),
      };
    case 'verify_start':
      return {
        title: 'Starting verification',
        details: rest.sample_count != null ? `Testing ${rest.sample_count} sample(s)` : null,
      };
    case 'verify_sample': {
      const verdict = rest.verdict === 'VERIFIED' ? 'Verified' : 'Not verified';
      const url = rest.html_url ? truncateUrl(rest.html_url) : '';
      return {
        title: `Sample ${(rest.index ?? 0) + 1}: ${verdict}`,
        details: [url, rest.reason].filter(Boolean).join(' — ') || null,
      };
    }
    case 'verify_complete':
      return {
        title: `Verification ${rest.verdict === 'VERIFIED' ? 'passed' : 'failed'}`,
        details: rest.reasoning || null,
      };
    case 'complete': {
      const ctx = rest.review_context || {};
      const verdict = ctx.repair_verdict;
      const partial = ctx.partial_repair;
      const perSample = ctx.per_sample_results || [];
      const fixed = perSample.filter((r) => r.status === 'fixed').length;
      const unfixed = perSample.filter((r) => r.status === 'unfixed').length;
      const fp = perSample.filter((r) => r.status === 'false_positive').length;
      const failing = perSample.filter((r) => !r.is_passing_sample).length;

      let title = 'Revision complete';
      if (verdict === 'VERIFIED') title += ' — all samples verified ✅';
      else if (verdict === 'PARTIALLY_VERIFIED' || partial) title += ' — partial fix ⚠️';
      else if (verdict) title += ` — ${verdict}`;

      const parts = [];
      if (failing > 0) parts.push(`${fixed}/${failing} fixed`);
      if (unfixed > 0) parts.push(`${unfixed} unfixed`);
      if (fp > 0) parts.push(`${fp} false positive`);
      if (ctx.repair_tool_calls) parts.push(`${ctx.repair_tool_calls} tool calls`);

      return {
        title,
        details: parts.length > 0 ? parts.join(' · ') : (ctx.repair_reasoning?.slice(0, 150) || null),
      };
    }
    case 'error':
      return {
        title: 'Error occurred',
        details: rest.message || JSON.stringify(rest),
      };
    default:
      return {
        title: type,
        details: Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : null,
      };
  }
}
